import React, { useState, useEffect } from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { useParams } from 'react-router';
import { GatewayVisibleSubsensors } from '../SensorConfig/helpers';
import { getAllLocationFloorplan, getCurrentLocation, getSensorsById } from '../../state/selectors';
import { SensorLatest } from '../../services/api';
import { appSwitchBtnHandler } from '../../Shell/helpers';
import { fetchSensorLatest } from '../../services/apiService';
import { themeProps } from '../../styles/theme';

import LocationSwitcher from '../../components/LocationSwitcher';
import PositioningMap from '../Map/PositioningMap';
import { setSensorsById } from '../../state/actions';
import { useAppDispatch, useAppSelector } from '../../state/store';
import { SensorPositionData } from '../Map/types';
import BaseSelection from '../HelperComponents/BaseSelection';

interface LocateContainerProps {
  selectedSensorId: string;
  clearSelectedSensor: () => void;
  gatewayDetails: SensorLatest | undefined;
  setGatewayDetails: (value: SensorLatest) => void;
  gatewayVisibleSubsensors: GatewayVisibleSubsensors[] | undefined;
  handleNext: () => void;
  position: SensorPositionData | undefined;
  setPosition: (value: SensorPositionData | undefined) => void;
}

function LocateContainer({
  selectedSensorId,
  clearSelectedSensor,
  gatewayDetails,
  setGatewayDetails,
  gatewayVisibleSubsensors,
  handleNext,
  position,
  setPosition,
}: LocateContainerProps): JSX.Element {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { sensorId } = useParams() as { sensorId: string };

  const currentLocation = useAppSelector(getCurrentLocation);
  const sensorsById = useAppSelector(getSensorsById);
  const allFloorPlan = useAppSelector(getAllLocationFloorplan);
  const [selectedGatewayId, setSelectedGatewayId] = useState<string>();

  // fetch gateway details
  useEffect(() => {
    if (selectedGatewayId) {
      closeSnackbar();
      const sensorDetails = sensorsById.get(selectedGatewayId);
      if (sensorDetails) setGatewayDetails(sensorDetails);
      else
        fetchSensorLatest(selectedGatewayId).then((details) => {
          setGatewayDetails(details);
          dispatch(setSensorsById([details]));
        });
    } else setGatewayDetails({ id: '' });
  }, [dispatch, selectedGatewayId, sensorsById, setGatewayDetails]);

  const btnClickHandler = () => {
    closeSnackbar();
    if (selectedGatewayId) handleNext();
    else
      enqueueSnackbar('Gateway or visible subsensor info not found', {
        variant: 'error',
        persist: true,
      });
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <Alert severity="info">Confirm your building / floor and gateway</Alert>
      <Box
        sx={{
          alignSelf: 'center',
          width: '100%',
          overflowX: 'scroll',
          padding: '5px 0',
        }}
      >
        <LocationSwitcher />
      </Box>
      <Box sx={{ width: '100%', margin: 'auto', padding: '10px' }}>
        <BaseSelection
          sensorId={selectedSensorId}
          selectedGatewayId={selectedGatewayId ?? ''}
          setGatewayId={setSelectedGatewayId}
          visibleSubSensors={gatewayVisibleSubsensors}
        />
        <PositioningMap
          floorPlan={allFloorPlan.get(currentLocation)}
          onChange={setPosition}
          position={position}
          mapName="SensorCreate"
          gatewayPosition={gatewayDetails?.position}
        />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            // can redirect to app view if sensor is selected from app through URL
            if (sensorId) appSwitchBtnHandler(dispatch);
            else clearSelectedSensor(); // clear selected sensor from new sensors list view and redirect to list
          }}
          startIcon={<KeyboardDoubleArrowLeftIcon />}
          style={{ margin: '0 10px' }}
        >
          Sensors List
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={btnClickHandler}
          endIcon={<KeyboardDoubleArrowRightIcon />}
          style={{
            margin: '0 10px',
            color: !gatewayDetails?.id ? themeProps.colors.lightGrey : theme.palette.text.secondary,
          }}
        >
          Next
        </Button>
      </Box>
    </div>
  );
}

export default LocateContainer;
